"use client";

import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import {
  Address,
  type Chain,
  type HttpTransport,
  parseUnits,
  type PrivateKeyAccount,
  type TypedDataParameter,
  type WalletClient,
} from "viem";

import { errorMessages } from "#/config/errorMessages";
import { type ChainId } from "#/config/web3/contractAddresses";
import { useWalletClient } from "#/hooks/useWalletClient";
import { useAccount } from "#/store/wallet/account/account";
import { SignedRequestWithDeadline } from "#/types/signatureRequest";
import { getPublicClient } from "#/utils/getPublicClient";
import { getSignatureDeadline } from "#/utils/getSignatureDeadline";
import { getDirectionIndex } from "#/utils/marketActionConverter";
import { EIP712Domain } from "#/utils/signature";

import { postRFQRequest } from "../kyan.service";
import { RFQRequestData } from "../kyan.types";
import { defaultApiResponseHandler } from "../kyan.utils";
import { useTradingSession } from "./useTradingSession";

export const RFQOrderType: TypedDataParameter[] = [
  { name: "instrumentName", type: "string" },
  { name: "size", type: "uint256" },
  { name: "direction", type: "uint8" },
];

export const PostRFQRequestType: TypedDataParameter[] = [
  { name: "deadline", type: "uint256" },
  { name: "taker", type: "address" },
  { name: "rfqOrders", type: "RFQOrderType[]" },
  { name: "duration", type: "uint256" },
];

const signRFQRequest = async (
  request: RFQRequestData,
  walletClient: WalletClient<HttpTransport, Chain, PrivateKeyAccount>,
): Promise<RFQRequestData & SignedRequestWithDeadline> => {
  const account = walletClient.account.address;
  const signature_deadline = getSignatureDeadline();

  try {
    const signature = await walletClient.signTypedData({
      account,
      domain: EIP712Domain[walletClient.chain.id as ChainId],
      types: { RFQOrderType, PostRFQRequestType },
      primaryType: "PostRFQRequestType",
      message: {
        deadline: signature_deadline,
        taker: request.taker,
        rfqOrders: request.rfq_orders.map((order) => ({
          instrumentName: order.instrument_name,
          size: parseUnits(
            (order.contracts ?? order.amount ?? "0").toString(),
            6,
          ),
          direction: getDirectionIndex(order.direction),
        })),
        duration: request.duration,
      },
    });

    return {
      ...request,
      signature_deadline,
      signature,
    };
  } catch (err) {
    console.error("Error signing RFQ request: ", err);
    if (String(err).includes("chainId should be same as current chainId")) {
      throw new Error(
        `Wrong chainId on signature: ${await walletClient.getChainId()}`,
      );
    }
    if (String(err).includes("UserRejectedRequestError")) {
      throw new Error("RFQ request signature rejected");
    }

    throw new Error("Error signing RFQ request");
  }
};

const validateRFQRequestSignature = async (
  request: RFQRequestData & SignedRequestWithDeadline,
  chainId: ChainId,
): Promise<boolean> => {
  const publicClient = getPublicClient(chainId);

  return publicClient.verifyTypedData({
    address: request.taker as Address,
    domain: EIP712Domain[chainId],
    types: { RFQOrderType, PostRFQRequestType },
    primaryType: "PostRFQRequestType",
    message: {
      deadline: request.signature_deadline,
      taker: request.taker,
      rfqOrders: request.rfq_orders.map((order) => ({
        instrumentName: order.instrument_name,
        size: parseUnits(
          (order.contracts ?? order.amount ?? "0").toString(),
          6,
        ),
        direction: getDirectionIndex(order.direction),
      })),
      duration: request.duration,
    },
    signature: request.signature,
  });
};

export const usePostRFQRequest = (
  options?: UseMutationOptions<string, Error, RFQRequestData>,
) => {
  const { account } = useAccount();
  const { walletClient } = useWalletClient();
  const { shouldUseSessionHash } = useTradingSession();

  return useMutation({
    ...options,
    mutationFn: async (request: RFQRequestData) => {
      if (!walletClient || !account) {
        throw new Error(errorMessages.WALLET_CLIENT_UNAVAILABLE);
      }

      if (shouldUseSessionHash) {
        return await defaultApiResponseHandler({
          serviceName: "Post RFQ Request",
          payload: {
            data: { ...request, taker: account! },
            includeSessionHash: true,
          },
          service: postRFQRequest,
        });
      } else {
        const signedData = await signRFQRequest(
          { ...request, taker: account! },
          walletClient as WalletClient<HttpTransport, Chain, PrivateKeyAccount>,
        );

        const isSignatureValid = await validateRFQRequestSignature(
          signedData,
          walletClient.chain?.id as ChainId,
        );

        return await defaultApiResponseHandler({
          serviceName: "Post RFQ Request",
          payload: { data: signedData },
          service: postRFQRequest,
          validation: [isSignatureValid],
        });
      }
    },
  });
};
